import { CalendarCellProps } from '@/components/calendar/calendar.types';
import { merge } from '@/helpers/Utility';
import { secondsToTimeString } from '@/helpers/dateHelper';
import { strings } from '@/services/translation/strings';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { TeamMemberPendingAvailabilityRequestCell } from './TeamMemberPendingAvailabilityRequestCell';
import { cellStyling } from './teamMemberCalendar.const';
import { useLeaveTypeCalendarDisplay } from './useLeaveTypeCalendarDisplay';
import { TeamMemberCalendarContextObject } from './useTeamMemberCalendarEvents';

export const TeamMemberCalendarContent: FC<CalendarCellProps<TeamMemberCalendarContextObject>> = ({
  date,
  contextObject
}) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const leaveType = useLeaveTypeCalendarDisplay(contextObject?.leaveType);

  if (!contextObject) {
    return <div></div>;
  }

  const hasAvailabilityMismatch = () => {
    if (contextObject.pendingAvailabilityRequest?.exceptions?.length) {
      return true;
    }

    if (!contextObject.availableTimes || contextObject.availableTimes.length === 0) return false;

    return contextObject.availableTimes.some(
      at => at.start !== contextObject.patternStart || at.end !== contextObject.patternEnd
    );
  };

  const borderClass = hasAvailabilityMismatch() ? 'border border-dotted border-gray-500' : '';

  if (contextObject.shifts?.length) {
    return (
      <div
        className="h-full flex justify-center items-center"
        style={leaveType.hasLeaveType ? { backgroundColor: leaveType.wrapperColor } : undefined}
      >
        <div className="flex flex-col items-center gap-1">
          {leaveType.badge}

          <div className="flex gap-1">
            <div className="h-full flex justify-center items-center">
              {contextObject.shifts.map((shift, index) => (
                <div key={index} className={twMerge('bg-primary-500 text-white rounded-md', cellStyling)}>
                  <p>{secondsToTimeString(shift.start)}</p>
                  <p>{secondsToTimeString(shift.end)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (leaveType.hasLeaveType) {
    return (
      <div className="h-full flex justify-center items-center" style={{ backgroundColor: leaveType.wrapperColor }}>
        {leaveType.card}
      </div>
    );
  }

  if (
    contextObject.pendingAvailabilityRequest &&
    contextObject.pendingAvailabilityRequest.exceptions &&
    contextObject.pendingAvailabilityRequest.exceptions?.length > 0
  ) {
    const pendingRequest = contextObject.pendingAvailabilityRequest.exceptions[0];
    return <TeamMemberPendingAvailabilityRequestCell exception={pendingRequest} />;
  }

  if (contextObject.availableTimes && contextObject.availableTimes.length > 0) {
    return (
      <div className="h-full flex justify-center items-center">
        {contextObject.availableTimes?.map((shift, index) => (
          <div key={index} className={twMerge(borderClass, cellStyling)}>
            <p>{secondsToTimeString(shift.start)}</p>
            <p>{secondsToTimeString(shift.end)}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={merge(
        'relative h-full w-full flex items-center justify-center',
        contextObject.hoverOnly ? 'opacity-0 hover:opacity-100' : ''
      )}
    >
      <div className="w-14 h-14 rounded-md flex flex-col items-center justify-center">
        <span>{contextObject.patternStart ? secondsToTimeString(contextObject.patternStart) : strings.common.na}</span>
        <span>{contextObject.patternEnd ? secondsToTimeString(contextObject.patternEnd) : strings.common.na}</span>
      </div>
    </div>
  );
};
